/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React from 'react';
// Leading icon per scope kind — disambiguates applicability without color-coding.
// Reserved enum→icon map (no magic strings at call sites). Covers both the rule
// axes (plural accounts/buildings + account_tag) and the singular check levels.
const SCOPE_ICON = {
    tenant: 'fa-light fa-building-shield',
    account: 'fa-light fa-briefcase',
    accounts: 'fa-light fa-briefcase',
    account_tag: 'fa-light fa-tags',
    building: 'fa-light fa-building',
    buildings: 'fa-light fa-building',
    building_tag: 'fa-light fa-tag'
};
/**
 * Explicit provenance label for the preview panel's "Resolved from" column
 * (IP-14303 #3). Uses the same scope-icon map but renders the inheritance chain
 * as readable English rather than the definition-scope label.
 */
function provenanceLabel(scope, t) {
    switch (scope) {
        case 'tenant':
            return t('rules.applies_to.provenance_tenant');
        case 'account':
        case 'accounts':
            return t('rules.applies_to.provenance_account');
        case 'account_tag':
            return t('rules.applies_to.provenance_account_tag');
        case 'building':
        case 'buildings':
            return t('rules.applies_to.provenance_building');
        case 'building_tag':
            return t('rules.applies_to.provenance_building_tag');
        default:
            return scope;
    }
}
/**
 * "Applies to" cell — two-line plain-text with typographic hierarchy.
 *
 * Design (rebuilt 2026-06-12): applicability is variable, free-form data, so it
 * is rendered as readable text (strong target + muted kind/count), NOT a colored
 * pill. Pills are reserved for bounded enum/status values elsewhere. Follows
 * Polaris hierarchy guidance and Cloudflare's effective-permissions pattern.
 *
 *   tenant       → target "All buildings"            meta "Tenant default"
 *   account      → target "Acme Fire Co."            meta "Account"
 *   building     → target "Electric Forest"          meta "Building"
 *   building_tag → target "Buildings tagged hospital" meta "3 buildings match"
 *
 * IP-14303 additions:
 *   resolvedFrom → shows explicit provenance: "Inherited from Tenant" /
 *                  "Inherited from Account" / "Inherited from tag: <tag>" etc.
 *   overridden   → shows visible "(Overridden at this scope)" badge
 *   onChangeScopeRequest (Task 3) → zero-match state shows an actionable CTA
 *     button so admins never need to silently invent a tag.
 */
export default function AppliesTo({ scopeLevel, targetLabel, matchCount, zeroMatch, resolvedFrom, overridden, onChangeScopeRequest, t }) {
    const target = (() => {
        switch (scopeLevel) {
            case 'account':
            case 'accounts':
                return targetLabel || t('rules.applies_to.unknown_account');
            case 'building':
            case 'buildings':
                return targetLabel || t('rules.applies_to.unknown_building');
            case 'building_tag':
                return t('rules.applies_to.target_tag', { tag: targetLabel !== null && targetLabel !== void 0 ? targetLabel : '' });
            case 'account_tag':
                return t('rules.applies_to.target_account_tag', { tag: targetLabel !== null && targetLabel !== void 0 ? targetLabel : '' });
            default:
                return t('rules.applies_to.target_tenant');
        }
    })();
    const meta = (() => {
        const isTagAxis = scopeLevel === 'building_tag' || scopeLevel === 'account_tag';
        if (isTagAxis) {
            if (zeroMatch) {
                return (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "tkb-status-flag--zero-match", "data-testid": "zero-match-badge" }, t('rules.applies_to.no_match')),
                    onChangeScopeRequest ? (React.createElement("button", { type: "button", className: "tkb-zero-match__cta", "data-testid": "zero-match-change-scope", onClick: onChangeScopeRequest, title: t('rules.applies_to.zero_match_cta_title') },
                        React.createElement("i", { className: "fa-light fa-arrow-right-to-arc", "aria-hidden": "true" }),
                        t('rules.applies_to.zero_match_cta'))) : (React.createElement("span", { className: "tkb-zero-match__hint", "data-testid": "zero-match-hint" }, t('rules.applies_to.zero_match_hint')))));
            }
            if (matchCount !== undefined && matchCount > 0) {
                return React.createElement("span", { className: "tkb-applies__count" }, t('rules.applies_to.match_count', { count: matchCount }));
            }
            return t(`rules.applies_to.kind_${scopeLevel}`);
        }
        return t(`rules.applies_to.kind_${scopeLevel}`);
    })();
    // IP-14303: explicit provenance text for the resolved-config preview.
    const provenance = resolvedFrom ? (React.createElement("span", { className: "tkb-applies__provenance", "data-testid": "provenance-label" },
        provenanceLabel(resolvedFrom, t),
        overridden && (React.createElement("span", { className: "tkb-applies__overridden", "data-testid": "overridden-indicator" },
            ' ',
            t('rules.applies_to.overridden_indicator'))))) : overridden ? (
    // overridden without resolvedFrom (e.g. rule list row where a more-specific
    // rule wins at the building or tag level)
    React.createElement("span", { className: "tkb-applies__overridden", "data-testid": "overridden-indicator" }, t('rules.applies_to.overridden_indicator'))) : null;
    return (React.createElement("span", { className: "tkb-applies", "data-testid": `applies-to-${scopeLevel}` },
        React.createElement("span", { className: "tkb-applies__target" }, target),
        React.createElement("span", { className: "tkb-applies__meta" },
            React.createElement("i", { className: SCOPE_ICON[scopeLevel], "aria-hidden": "true" }),
            meta),
        provenance));
}
